import { useEffect, useState, useCallback } from "react";
import { FiSearch, FiPlus, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../../../_config/api";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import Spinner from "../../../../utils/Loader";

function AllTokenQrList() {
  const [token, setToken] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    id: null,
    isDisable: null,
    message: "",
  });

  const fetchToken = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth("get", "/admin/getAllQrCodes", {
          isUpi: false,
        });
        const qrData = response.data || [];
        setToken(qrData);
        setTotal(response.totalCount || qrData.length);
      } catch (err) {
        showErrorToast(err.message || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    },
    [searchTerm, pageSize],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchToken(currentPage);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [currentPage, searchTerm, fetchToken]);

  // Status Toggle Logic
  const handleStatusToggle = async () => {
    const { id } = confirmModal;
    try {
      await makeRequestAuth("post", "/admin/updateQrCodeStatus", { id });

      setToken((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, isDisable: !item.isDisable } : item,
        ),
      );

      showSuccessToast("Status updated successfully");
    } catch (err) {
      showErrorToast("Failed to update status");
    } finally {
      closeConfirmModal();
    }
  };

  const openStatusModal = (item) => {
    setConfirmModal({
      open: true,
      id: item._id,
      isDisable: item.isDisable,
      message: `Are you sure you want to ${item.isDisable ? "Activate" : "Deactivate"} this address?`,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, id: null, isDisable: null, message: "" });
  };

  const getNetworkStyle = (net) => {
    switch (net) {
      case "ERC20":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "BEP20":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      default:
        return "bg-red-100 text-red-700 border-red-200"; // TRC20 default
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Token QR Management
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total {total} entries found
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
            <Link to="/qr-code/create">
              <button className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-xl font-medium shadow-lg hover:scale-[1.02] transition">
                <FiPlus size={18} />
                <span>Add TOKEN</span>
              </button>
            </Link>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 w-16 text-center">#</th>
                  <th className="py-4 px-6">Address</th>
                  <th className="py-4 px-6 text-center">Network</th>
                  <th className="py-4 px-6 text-center">Status</th>
                  <th className="py-4 px-6 text-right">Toggle Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {token.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-gray-500">
                      No Data Found
                    </td>
                  </tr>
                ) : (
                  token.map((item, index) => (
                    <tr
                      key={item._id}
                      className={`hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition ${item.isDisable ? "opacity-60" : ""}`}
                    >
                      <td className="py-4 px-6 text-center">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900 dark:text-white font-mono text-sm break-all">
                        {item.address}
                      </td>

                      {/* NETWORK COLUMN ADDED */}
                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-3 py-1 rounded-full border ${getNetworkStyle(item.network || "TRC20")}`}
                        >
                          {item.network || "TRC20"}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded ${item.isDisable ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"}`}
                        >
                          {item.isDisable ? "INACTIVE" : "ACTIVE"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        {/* TOGGLE SWITCH */}
                        <div className="flex justify-end items-center">
                          <button
                            onClick={() => openStatusModal(item)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${!item.isDisable ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
                          >
                            <span
                              className={`${!item.isDisable ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- STATUS CONFIRMATION MODAL --- */}
      {confirmModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center border border-gray-200 dark:border-gray-600">
            <div
              className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${confirmModal.isDisable ? "bg-emerald-100 text-emerald-600" : "bg-yellow-100 text-yellow-600"}`}
            >
              {confirmModal.isDisable ? (
                <FiCheckCircle size={32} />
              ) : (
                <FiAlertCircle size={32} />
              )}
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Update Status?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              {confirmModal.message}
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={closeConfirmModal}
                className="flex-1 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusToggle}
                className={`flex-1 px-5 py-2.5 rounded-xl text-white font-medium shadow-lg transition ${confirmModal.isDisable ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-500 hover:bg-red-600"}`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllTokenQrList;
